/**
 * Airfoil SOURCES for the "Replace foil" picker (FXC-9009): the bundled Flexfoil/UIUC catalog and a
 * Selig/Lednicer `.dat` parser. Both produce a raw coordinate loop that the caller feeds through the
 * existing {@link conditionFoil} (Selig/Lednicer auto-detect → normalise → TE-close) before use, so
 * canonicalisation lives in one place and these stay thin readers.
 *
 * The catalog is a static asset (`public/airfoils/catalog.json`, ~1665 foils) generated from
 * Flexfoil's shipped UIUC database by `scripts/build-airfoil-catalog.py`. It is lazy-loaded on first
 * use so the ~1.6 MB payload never enters the main bundle.
 */
import type { V2 } from './estolConfig';

/** A catalog foil: a display name + a flat [x0,y0,x1,y1,…] coordinate loop (header/counts stripped). */
export interface AirfoilCatalogEntry {
  name: string;
  points: number[];
}

/** Reshape a flat [x0,y0,x1,y1,…] coordinate array into a {@link V2}[] loop. */
export function reshapePoints(flat: number[]): V2[] {
  const out: V2[] = [];
  for (let i = 0; i + 1 < flat.length; i += 2) out.push([flat[i]!, flat[i + 1]!]);
  return out;
}

/**
 * Parse a Selig or Lednicer `.dat` airfoil file into a raw coordinate loop. Drops the name/header
 * line(s) and the Lednicer point-count line (values well outside the |x|,|y| ≤ 2 normalised range),
 * keeping only `x y` coordinate pairs. The ordering (Selig single-loop vs Lednicer two-block) is left
 * to {@link conditionFoil}, which the caller runs next.
 */
export function parseAirfoilDat(text: string): V2[] {
  const pts: V2[] = [];
  for (const rawLine of text.split(/\r?\n/)) {
    const s = rawLine.trim();
    if (!s) continue;
    const parts = s.split(/[\s,]+/);
    if (parts.length !== 2) continue; // header lines, comments, multi-column junk
    const x = Number(parts[0]);
    const y = Number(parts[1]);
    if (!Number.isFinite(x) || !Number.isFinite(y)) continue; // header text
    if (Math.abs(x) > 2 || Math.abs(y) > 2) continue; // Lednicer count line / scale junk
    pts.push([x, y]);
  }
  return pts;
}

let _catalogPromise: Promise<AirfoilCatalogEntry[]> | null = null;

/**
 * Lazy-load the bundled airfoil catalog (cached after the first call). Returns [] if the asset is
 * missing/unreachable so the picker degrades to the built-ins + NACA + .dat upload rather than throwing.
 */
export function loadAirfoilCatalog(): Promise<AirfoilCatalogEntry[]> {
  if (!_catalogPromise) {
    _catalogPromise = fetch(`${import.meta.env.BASE_URL ?? '/'}airfoils/catalog.json`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`catalog ${r.status}`))))
      .then((data) => (Array.isArray(data) ? (data as AirfoilCatalogEntry[]) : []))
      .catch(() => {
        _catalogPromise = null; // allow a retry on a later open
        return [];
      });
  }
  return _catalogPromise;
}
